import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import React from 'react';
import { Redirect } from 'react-router-dom';
import { formatVertical } from '@moda/portal-stanchions';
import {
  AvailabilityInput,
  DesignersIndexPageQuery,
  DesignersIndexPageQueryVariables
} from '../../generated/types';
import { usePLPParams } from '../../hooks/usePLPParams';
import { PageMeta } from '../../components/PageMeta';
import { DesignerListing, DESIGNER_LISTING_PAGE_FRAGMENT } from '../../components/DesignerListing';
import { NotFoundPage } from '../NotFoundPage';
import { hrefFor } from '../../routers';
import { useShowSaleOnPLP } from '../../hooks/useShowSaleOnPLP';

const DESIGNERS_INDEX_PAGE_QUERY: TypedDocumentNode<
  DesignersIndexPageQuery,
  DesignersIndexPageQueryVariables
> = gql`
  query DesignersIndexPageQuery(
    $availability: [AvailabilityInput]
    $categoryPath: String
    $noSale: Boolean
  ) {
    designersIndex: designers_az(
      input: { availability: $availability, category_path: $categoryPath, no_sale: $noSale }
    ) {
      ...DesignerListingPageFragment
    }
  }
  ${DESIGNER_LISTING_PAGE_FRAGMENT}
`;

export const DesignerIndexPage: React.FC = () => {
  const {
    queryVariables,
    params: { vertical = '' }
  } = usePLPParams();

  if (queryVariables.availability == null || queryVariables.availability.length === 0) {
    queryVariables.availability = [
      AvailabilityInput.available_now,
      AvailabilityInput.preorder,
      AvailabilityInput.trunkshow
    ];
  }

  const showSaleInPLPs = useShowSaleOnPLP();

  const {
    data: currentData,
    previousData,
    loading,
    error
  } = useQuery(DESIGNERS_INDEX_PAGE_QUERY, {
    variables: { ...queryVariables, noSale: !showSaleInPLPs }
  });

  const data = currentData ?? previousData;

  const header = `Shop for ${formatVertical(vertical, true)} ${
    vertical === 'home' ? 'Decor' : 'Fashion'
  } Designers and Brands`;

  if (vertical === 'men') {
    return <Redirect to={hrefFor.HomePage()} />;
  }

  if (data && !data.designersIndex) return <NotFoundPage />;

  return (
    <>
      <PageMeta
        title={header}
        description="Discover & shop all the designers and brands on Moda Operandi, including fashion, jewelry and home decor."
      />

      <DesignerListing
        headerText={header}
        queryResult={data?.designersIndex}
        error={error}
        loading={loading}
      />
    </>
  );
};
